import { useState, useCallback, useEffect } from 'react';
import { ShareCardGenerator } from '@/components/sharing/ShareCardGenerator';
import { Copy, Check, Share2, ChevronDown, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { useUserCredits } from '@/hooks/useUserCredits';
import { useEventTracking } from '@/hooks/useEventTracking';
import { buildShareLinks } from '@/lib/buildShareLinks';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { PrivateShareManager } from '@/components/sharing/PrivateShareManager';

const copy = {
  en: {
    title: 'Share Announcement',
    subtitle: "Share privately or publicly — you're in control.",
    copyLink: 'Copy Link',
    shareUrl: 'Share URL',
    linkCopied: 'Link copied',
    copyToClipboard: 'Copy link to clipboard',
    shareOnSocial: 'Share on Social',
    shareOn: 'Share on',
    captionQuestion: 'Need help with a caption?',
    generating: 'Generating...',
    captionCta: 'Help me write a caption',
    generateCaptions: 'Generate caption suggestions',
    tapToCopy: 'Tap a caption to copy it.',
    copyCaption: 'Copy caption',
    captionCopied: 'Caption copied!',
    captionCopyFail: "Couldn't copy caption.",
    linkCopySuccess: 'Link copied. Share when it feels right.',
    linkCopyFail: "Couldn't copy. Tap and hold the link to copy it.",
    footer: "You're always in control — you choose where and when it's shared.",
    fallbackCaption1: 'A message worth reading. 💛',
    fallbackCaption2: 'Some things are better said now.',
    fallbackCaption3: 'Written from the heart.',
  },
  es: {
    title: 'Compartir Anuncio',
    subtitle: 'Comparte de forma privada o pública — tú tienes el control.',
    copyLink: 'Copiar Enlace',
    shareUrl: 'URL para compartir',
    linkCopied: 'Enlace copiado',
    copyToClipboard: 'Copiar enlace al portapapeles',
    shareOnSocial: 'Compartir en Redes',
    shareOn: 'Compartir en',
    captionQuestion: '¿Necesitas ayuda con una descripción?',
    generating: 'Generando...',
    captionCta: 'Ayúdame a escribir una descripción',
    generateCaptions: 'Generar sugerencias de descripción',
    tapToCopy: 'Toca una descripción para copiarla.',
    copyCaption: 'Copiar descripción',
    captionCopied: '¡Descripción copiada!',
    captionCopyFail: 'No se pudo copiar la descripción.',
    linkCopySuccess: 'Enlace copiado. Compártelo cuando sientas que es el momento.',
    linkCopyFail: 'No se pudo copiar. Mantén presionado el enlace para copiarlo.',
    footer: 'Siempre tienes el control — tú decides dónde y cuándo se comparte.',
    fallbackCaption1: 'Un mensaje que vale la pena leer. 💛',
    fallbackCaption2: 'Algunas cosas es mejor decirlas ahora.',
    fallbackCaption3: 'Escrito desde el corazón.',
  },
  fr: {
    title: 'Partager l\'Annonce',
    subtitle: 'Partagez en privé ou publiquement — vous gardez le contrôle.',
    copyLink: 'Copier le Lien',
    shareUrl: 'URL de partage',
    linkCopied: 'Lien copié',
    copyToClipboard: 'Copier le lien',
    shareOnSocial: 'Partager sur les Réseaux',
    shareOn: 'Partager sur',
    captionQuestion: 'Besoin d\'aide pour une légende ?',
    generating: 'Génération...',
    captionCta: 'Aidez-moi à écrire une légende',
    generateCaptions: 'Générer des suggestions de légendes',
    tapToCopy: 'Appuyez sur une légende pour la copier.',
    copyCaption: 'Copier la légende',
    captionCopied: 'Légende copiée !',
    captionCopyFail: 'Impossible de copier la légende.',
    linkCopySuccess: 'Lien copié. Partagez quand vous le sentez.',
    linkCopyFail: 'Impossible de copier. Appuyez longuement sur le lien pour le copier.',
    footer: 'Vous gardez toujours le contrôle — vous choisissez où et quand partager.',
    fallbackCaption1: 'Un message qui vaut la peine d\'être lu. 💛',
    fallbackCaption2: 'Certaines choses sont mieux dites maintenant.',
    fallbackCaption3: 'Écrit du fond du cœur.',
  },
  pt: {
    title: 'Compartilhar Anúncio',
    subtitle: 'Compartilhe de forma privada ou pública — você tem o controle.',
    copyLink: 'Copiar Link',
    shareUrl: 'URL de compartilhamento',
    linkCopied: 'Link copiado',
    copyToClipboard: 'Copiar link para a área de transferência',
    shareOnSocial: 'Compartilhar nas Redes',
    shareOn: 'Compartilhar no',
    captionQuestion: 'Precisa de ajuda com uma legenda?',
    generating: 'Gerando...',
    captionCta: 'Me ajude a escrever uma legenda',
    generateCaptions: 'Gerar sugestões de legendas',
    tapToCopy: 'Toque em uma legenda para copiá-la.',
    copyCaption: 'Copiar legenda',
    captionCopied: 'Legenda copiada!',
    captionCopyFail: 'Não foi possível copiar a legenda.',
    linkCopySuccess: 'Link copiado. Compartilhe quando sentir que é o momento.',
    linkCopyFail: 'Não foi possível copiar. Toque e segure o link para copiá-lo.',
    footer: 'Você sempre tem o controle — você escolhe onde e quando compartilhar.',
    fallbackCaption1: 'Uma mensagem que vale a pena ler. 💛',
    fallbackCaption2: 'Algumas coisas é melhor dizer agora.',
    fallbackCaption3: 'Escrito do coração.',
  },
  ar: {
    title: 'مشاركة الإعلان',
    subtitle: 'شارك بشكل خاص أو عام — أنت تتحكم.',
    copyLink: 'نسخ الرابط',
    shareUrl: 'رابط المشاركة',
    linkCopied: 'تم نسخ الرابط',
    copyToClipboard: 'نسخ الرابط إلى الحافظة',
    shareOnSocial: 'مشاركة على وسائل التواصل',
    shareOn: 'مشاركة على',
    captionQuestion: 'هل تحتاج مساعدة في كتابة وصف؟',
    generating: 'جارٍ الإنشاء...',
    captionCta: 'ساعدني في كتابة وصف',
    generateCaptions: 'إنشاء اقتراحات وصف',
    tapToCopy: 'انقر على وصف لنسخه.',
    copyCaption: 'نسخ الوصف',
    captionCopied: 'تم نسخ الوصف!',
    captionCopyFail: 'تعذر نسخ الوصف.',
    linkCopySuccess: 'تم نسخ الرابط. شاركه عندما تشعر أنه الوقت المناسب.',
    linkCopyFail: 'تعذر النسخ. اضغط مطولاً على الرابط لنسخه.',
    footer: 'أنت دائماً تتحكم — أنت تختار أين ومتى يتم المشاركة.',
    fallbackCaption1: 'رسالة تستحق القراءة. 💛',
    fallbackCaption2: 'بعض الأشياء من الأفضل قولها الآن.',
    fallbackCaption3: 'مكتوب من القلب.',
  },
  ja: {
    title: 'アナウンスを共有',
    subtitle: 'プライベートでもパブリックでも — あなたがコントロールします。',
    copyLink: 'リンクをコピー',
    shareUrl: '共有URL',
    linkCopied: 'リンクをコピーしました',
    copyToClipboard: 'リンクをクリップボードにコピー',
    shareOnSocial: 'SNSで共有',
    shareOn: '共有先',
    captionQuestion: 'キャプションのお手伝いが必要ですか？',
    generating: '生成中...',
    captionCta: 'キャプションの作成を手伝って',
    generateCaptions: 'キャプション候補を生成',
    tapToCopy: 'キャプションをタップしてコピー。',
    copyCaption: 'キャプションをコピー',
    captionCopied: 'キャプションをコピーしました！',
    captionCopyFail: 'キャプションのコピーに失敗しました。',
    linkCopySuccess: 'リンクをコピーしました。お好きなタイミングで共有してください。',
    linkCopyFail: 'コピーできませんでした。リンクを長押ししてコピーしてください。',
    footer: 'いつでもあなたがコントロールしています。いつどこで共有するかを選べます。',
    fallbackCaption1: '読む価値のあるメッセージです。💛',
    fallbackCaption2: '今言うのが一番いいことがあります。',
    fallbackCaption3: '心を込めて書きました。',
  },
  ko: {
    title: '공지 공유',
    subtitle: '비공개 또는 공개로 공유하세요 — 항상 당신이 결정합니다.',
    copyLink: '링크 복사',
    shareUrl: '공유 URL',
    linkCopied: '링크가 복사되었습니다',
    copyToClipboard: '클립보드에 링크 복사',
    shareOnSocial: 'SNS에서 공유',
    shareOn: '공유하기',
    captionQuestion: '캡션 작성에 도움이 필요하신가요?',
    generating: '생성 중...',
    captionCta: '캡션 작성을 도와주세요',
    generateCaptions: '캡션 제안 생성',
    tapToCopy: '캡션을 탭하여 복사하세요.',
    copyCaption: '캡션 복사',
    captionCopied: '캡션이 복사되었습니다!',
    captionCopyFail: '캡션을 복사할 수 없습니다.',
    linkCopySuccess: '링크가 복사되었습니다. 적절한 때에 공유하세요.',
    linkCopyFail: '복사할 수 없습니다. 링크를 길게 눌러 복사하세요.',
    footer: '언제나 당신이 결정합니다 — 어디서 언제 공유할지 선택하세요.',
    fallbackCaption1: '읽을 가치가 있는 메시지입니다. 💛',
    fallbackCaption2: '어떤 것은 지금 말하는 게 더 좋습니다.',
    fallbackCaption3: '마음을 담아 쓴 글입니다.',
  },
  id: {
    title: 'Bagikan Pengumuman',
    subtitle: 'Bagikan secara pribadi atau publik — Anda yang mengontrol.',
    copyLink: 'Salin Tautan',
    shareUrl: 'URL Berbagi',
    linkCopied: 'Tautan disalin',
    copyToClipboard: 'Salin tautan ke clipboard',
    shareOnSocial: 'Bagikan di Media Sosial',
    shareOn: 'Bagikan di',
    captionQuestion: 'Butuh bantuan menulis caption?',
    generating: 'Membuat...',
    captionCta: 'Bantu saya menulis caption',
    generateCaptions: 'Buat saran caption',
    tapToCopy: 'Ketuk caption untuk menyalinnya.',
    copyCaption: 'Salin caption',
    captionCopied: 'Caption disalin!',
    captionCopyFail: 'Tidak dapat menyalin caption.',
    linkCopySuccess: 'Tautan disalin. Bagikan saat Anda merasa siap.',
    linkCopyFail: 'Tidak dapat menyalin. Tekan dan tahan tautan untuk menyalinnya.',
    footer: 'Anda selalu yang mengontrol — Anda yang memilih kapan dan di mana untuk berbagi.',
    fallbackCaption1: 'Pesan yang layak dibaca. 💛',
    fallbackCaption2: 'Beberapa hal lebih baik dikatakan sekarang.',
    fallbackCaption3: 'Ditulis dari hati.',
  },

  hi: {
    title: 'घोषणा साझा करें',
    subtitle: "Share privately or publicly — you're in control.",
    copyLink: 'लिंक कॉपी करें',
    shareUrl: 'URL साझा करें',
    linkCopied: 'लिंक कॉपी किया गया',
    copyToClipboard: 'लिंक को क्लिपबोर्ड पर कॉपी करें',
    shareOnSocial: 'सोशल पर साझा करें',
    shareOn: 'पर साझा करें',
    captionQuestion: 'कैप्शन में मदद चाहिए?',
    generating: 'उत्पन्न हो रहा है...',
    captionCta: 'कैप्शन लिखने में मेरी मदद करें',
    generateCaptions: 'कैप्शन सुझाव उत्पन्न करें',
    tapToCopy: 'कॉपी करने के लिए कैप्शन पर टैप करें।',
    copyCaption: 'कैप्शन कॉपी करें',
    captionCopied: 'कैप्शन कॉपी हो गया!',
    captionCopyFail: "Couldn't copy caption.",
    linkCopySuccess: 'लिंक कॉपी हो गया। जब सही लगे तब साझा करें।',
    linkCopyFail: "Couldn't copy. Tap and hold the link to copy it.",
    footer: "You're always in control — you choose where and when it's shared.",
    fallbackCaption1: 'पढ़ने लायक एक संदेश। 💛',
    fallbackCaption2: 'कुछ बातें अभी कहना बेहतर है।',
    fallbackCaption3: 'दिल से लिखा गया।',
  },
};

// Platform icons as inline SVGs for clean rendering
function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

interface ShareAnnouncementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  announcementId?: string | null;
  publicSlug: string;
  recipientName: string;
  message?: string;
  template?: string;
  relationship?: string;
  source: 'dashboard_card' | 'announcement_view' | 'recipient_page' | 'tree_view';
}

export function ShareAnnouncementModal({
  open,
  onOpenChange,
  announcementId,
  publicSlug,
  recipientName,
  message,
  template,
  relationship,
  source,
}: ShareAnnouncementModalProps) {
  const [copied, setCopied] = useState(false);
  const [captionOpen, setCaptionOpen] = useState(false);
  const [captions, setCaptions] = useState<string[]>([]);
  const [captionLoading, setCaptionLoading] = useState(false);
  const { language } = useLanguage();
  const { user } = useAuth();
  const c = lc(copy, language);
  const { credits } = useUserCredits();
  const { trackEvent } = useEventTracking();

  const userPlan = credits?.highest_bundle_purchased || 'free';
  const senderName = user?.user_metadata?.full_name || user?.user_metadata?.name || null;
  const links = buildShareLinks({
    publicSlug,
    templateSlug: template,
    relationship,
    senderName,
  });
  const shareLinkTarget = typeof window !== 'undefined' && window.self !== window.top ? '_top' : '_blank';

  // Common tracking props (no PII)
  const baseProps = {
    announcement_id: announcementId || 'unknown',
    public_id: publicSlug,
    template_slug: template || 'unknown',
    relationship: relationship || 'unknown',
    user_plan: userPlan,
    locale: language,
  };

  // Track modal open
  useEffect(() => {
    if (open) {
      trackEvent('share_modal_opened', { ...baseProps, source });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        trackEvent('share_modal_closed', {
          announcement_id: announcementId || 'unknown',
          public_id: publicSlug,
          close_method: 'x', // default; escape/backdrop handled by radix
        });
      }
      onOpenChange(nextOpen);
    },
    [announcementId, publicSlug, onOpenChange, trackEvent]
  );

  const handleCopyLink = async () => {
    trackEvent('share_copy_clicked', baseProps);
    try {
      await navigator.clipboard.writeText(links.canonical);
      setCopied(true);
      trackEvent('share_copy_succeeded', {
        announcement_id: announcementId || 'unknown',
        public_id: publicSlug,
      });
      toast.success(c.linkCopySuccess);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      trackEvent('share_copy_failed', {
        announcement_id: announcementId || 'unknown',
        public_id: publicSlug,
        error_message: errorMessage,
      });
      toast.error(c.linkCopyFail);
    }
  };

  const handlePlatformTracking = (platform: 'facebook' | 'x' | 'linkedin' | 'whatsapp') => {
    trackEvent('share_platform_clicked', { ...baseProps, platform });
    trackEvent('share_platform_opened', {
      platform,
      announcement_id: announcementId || 'unknown',
      public_id: publicSlug,
    });
  };

  const handlePlatformLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    platform: 'facebook' | 'x' | 'linkedin' | 'whatsapp',
    url: string
  ) => {
    handlePlatformTracking(platform);

    const isEmbedded = typeof window !== 'undefined' && window.self !== window.top;
    if (!isEmbedded) return;

    event.preventDefault();
    try {
      if (window.top) {
        window.top.location.href = url;
        return;
      }
    } catch {
      // ignore and fallback
    }
    window.location.href = url;
  };

  const handleGenerateCaptions = async () => {
    setCaptionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('announcement-ai-assist', {
        body: {
          action: 'generate_captions',
          template: template || 'classic',
          relationship: relationship || 'friend',
          locale: language,
        },
      });

      if (error) throw error;
      setCaptions(data?.captions || [c.fallbackCaption1, c.fallbackCaption2, c.fallbackCaption3]);
    } catch {
      setCaptions([c.fallbackCaption1, c.fallbackCaption2, c.fallbackCaption3]);
    } finally {
      setCaptionLoading(false);
    }
  };

  const handleCopyCaption = async (caption: string) => {
    try {
      await navigator.clipboard.writeText(caption);
      toast.success(c.captionCopied);
    } catch {
      toast.error(c.captionCopyFail);
    }
  };

  const platforms = [
    { key: 'facebook' as const, label: 'Facebook', Icon: FacebookIcon },
    { key: 'x' as const, label: 'X', Icon: XIcon },
    { key: 'linkedin' as const, label: 'LinkedIn', Icon: LinkedInIcon },
    { key: 'whatsapp' as const, label: 'WhatsApp', Icon: WhatsAppIcon },
  ];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-primary" />
            {c.title}
          </DialogTitle>
          <DialogDescription>
            {c.subtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Copy Link */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{c.copyLink}</label>
            <div className="flex gap-2">
              <Input
                readOnly
                value={links.canonical}
                className="flex-1 bg-muted text-sm"
                aria-label={c.shareUrl}
              />
              <Button
                onClick={handleCopyLink}
                variant="outline"
                size="icon"
                className="shrink-0"
                aria-label={copied ? c.linkCopied : c.copyToClipboard}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-primary" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <Separator />

          {/* Social Share Buttons */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">{c.shareOnSocial}</label>
             <div className="grid grid-cols-2 gap-2">
              {platforms.map(({ key, label, Icon }) => (
                <Button
                  key={key}
                  variant="outline"
                  className="gap-2 justify-start"
                  asChild
                >
                  <a
                    href={links[key]}
                    target={shareLinkTarget}
                    rel="noopener noreferrer"
                    onClick={(event) => handlePlatformLinkClick(event, key, links[key])}
                    aria-label={`${c.shareOn} ${label}`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </a>
                </Button>
              ))}
            </div>
          </div>

          <Separator />

          {/* Caption Helper */}
          <Collapsible open={captionOpen} onOpenChange={setCaptionOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between gap-2 text-sm text-muted-foreground hover:text-foreground">
                <span className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                   {c.captionQuestion}
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${captionOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-2">
              {captions.length === 0 ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full gap-2"
                  onClick={handleGenerateCaptions}
                  disabled={captionLoading}
                  aria-label={c.generateCaptions}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {captionLoading ? c.generating : c.captionCta}
                </Button>
              ) : (
                <div className="space-y-2">
                  {captions.map((caption, i) => (
                    <button
                      key={i}
                      onClick={() => handleCopyCaption(caption)}
                      className="w-full text-left text-sm p-3 rounded-lg border border-border bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                      aria-label={`${c.copyCaption}: ${caption}`}
                    >
                      {caption}
                    </button>
                  ))}
                  <p className="text-xs text-muted-foreground text-center">{c.tapToCopy}</p>
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>

          {/* Private Sharing */}
          {user && announcementId && (
            <>
              <Separator />
              <PrivateShareManager
                announcementId={announcementId}
                publicSlug={publicSlug}
              />
            </>
          )}

          {/* Share Card Download */}
          {message && (
            <>
              <Separator />
              <ShareCardGenerator
                message={message}
                recipientName={recipientName}
                className="w-full"
                senderName={senderName}
                relationship={relationship}
              />
            </>
          )}
        </div>

        {/* Footer reassurance */}
        <div className="border-t border-border pt-4">
          <p className="text-xs text-muted-foreground text-center">
            {c.footer}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
